import React, { CSSProperties, useEffect, useState } from 'react';
import { borderRadius } from '../../theme/borderRadius';
import { colors, hexToRgbA, mixColors } from '../../theme/colors';
import { getAvatarColor, getInitials } from './utils';

type ProfileImageBackgroundVariant = 'translucent' | 'opaque';
type ProfileImageStyle = CSSProperties | Record<string, unknown>;

interface ProfileImageProps {
  src?: string | null;
  username?: string;
  style?: ProfileImageStyle;
  size?: number;
  backgroundVariant?: ProfileImageBackgroundVariant;
}

const getNumericStyleValue = (value: unknown) => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = parseFloat(value);
    return Number.isNaN(parsedValue) ? undefined : parsedValue;
  }

  return undefined;
};

const getProfileImageLayout = ({
  size,
  containerStyle,
}: {
  size: number;
  containerStyle?: ProfileImageStyle;
}) => {
  const containerBorderRadius =
    getNumericStyleValue(containerStyle?.borderRadius) ?? borderRadius.large;
  const containerWidth = getNumericStyleValue(containerStyle?.width) ?? size;
  const containerHeight = getNumericStyleValue(containerStyle?.height) ?? size;
  const borderWidth = getNumericStyleValue(containerStyle?.borderWidth) ?? 0;
  const imageInset = borderWidth * 2;
  const containerSize = Math.min(containerWidth, containerHeight);
  const actualImageSize = containerSize - imageInset;
  const imageBorderRadius =
    containerSize > 0
      ? containerBorderRadius * (actualImageSize / containerSize)
      : containerBorderRadius;

  return {
    containerBorderRadius,
    containerWidth,
    containerHeight,
    containerSize,
    actualImageSize,
    imageBorderRadius,
  };
};

const ProfileImage: React.FC<ProfileImageProps> = ({
  src,
  username,
  style,
  size = 40,
  backgroundVariant = 'translucent',
}) => {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const hasImage = Boolean(src) && !imageLoadFailed;
  const avatarColor = getAvatarColor({ username });
  const initials = getInitials({ username });
  const isOpaqueBackground = backgroundVariant === 'opaque';
  const fallbackBackgroundColor = isOpaqueBackground
    ? mixColors({
        colors: [
          { color: colors.backgroundMediumLight, weight: 0.82 },
          { color: avatarColor, weight: 0.18 },
        ],
      })
    : hexToRgbA(avatarColor, 0.18);
  const {
    containerBorderRadius,
    containerWidth,
    containerHeight,
    containerSize,
    actualImageSize,
    imageBorderRadius,
  } = getProfileImageLayout({ size, containerStyle: style });
  const styleOverrides = style as CSSProperties | undefined;

  useEffect(() => {
    setImageLoadFailed(false);
  }, [src]);

  const baseContainerStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    overflow: 'hidden',
    width: containerWidth,
    height: containerHeight,
    borderRadius: containerBorderRadius,
  };

  if (hasImage) {
    return (
      <div
        style={{
          ...baseContainerStyle,
          backgroundColor: isOpaqueBackground
            ? colors.backgroundMediumLight
            : undefined,
          ...styleOverrides,
        }}
      >
        <img
          src={src || undefined}
          alt={username || ''}
          onError={() => setImageLoadFailed(true)}
          style={{
            width: actualImageSize,
            height: actualImageSize,
            borderRadius: imageBorderRadius,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        ...baseContainerStyle,
        backgroundColor: fallbackBackgroundColor,
        borderWidth: styleOverrides?.borderWidth ?? 1,
        borderStyle: styleOverrides?.borderStyle ?? 'solid',
        borderColor:
          styleOverrides?.borderColor ?? hexToRgbA(colors.textPrimary, 0.05),
        ...styleOverrides,
      }}
    >
      <span
        style={{
          color: avatarColor,
          fontSize: containerSize * 0.45,
          lineHeight: 1,
          fontFamily: 'inherit',
          userSelect: 'none',
          textTransform: 'uppercase',
          display: 'block',
          marginTop: 1,
          letterSpacing: 0,
          fontWeight: 800,
        }}
      >
        {initials}
      </span>
    </div>
  );
};

export default ProfileImage;
